import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';

import { RecognitionCardItem, useToast } from '@aslanpay/portal-common-lib';

import { isAllowedCardImage } from 'app/utils/recognitionCardUpload';

import EmojiPickerInput from './EmojiPickerInput';
import { ManagedRecognitionValue, RecognitionValueFormState } from './types';

interface RecognitionValueModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingValue: ManagedRecognitionValue | null;
  availableCards: RecognitionCardItem[];
  showEnabledToggle: boolean;
  isSaving: boolean;
  onSubmit: (form: RecognitionValueFormState) => Promise<void>;
  onUploadCard: (file: File) => Promise<string | null>;
}

const EMPTY_FORM: RecognitionValueFormState = {
  name: '',
  description: '',
  emoji: '',
  allowGifs: false,
  enabled: true,
  cardIds: [],
};

function buildInitialForm(value: ManagedRecognitionValue | null): RecognitionValueFormState {
  if (!value) {
    return EMPTY_FORM;
  }

  return {
    name: value.name,
    description: value.description ?? '',
    emoji: value.emoji,
    allowGifs: value.allowGifs,
    enabled: value.enabled ?? true,
    cardIds: value.cards.map((card) => card.id),
  };
}

function RecognitionValueModal({
  isOpen,
  onClose,
  editingValue,
  availableCards,
  showEnabledToggle,
  isSaving,
  onSubmit,
  onUploadCard,
}: RecognitionValueModalProps) {
  const toast = useToast();

  const [form, setForm] = useState<RecognitionValueFormState>(buildInitialForm(editingValue));
  const [isUploadingCard, setIsUploadingCard] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialForm(editingValue));
      setSubmitAttempted(false);
      setIsDragging(false);
    }
  }, [isOpen, editingValue]);

  const isNameValid = form.name.trim().length > 0;
  const isEmojiValid = form.emoji.trim().length > 0;
  const isFormValid = isNameValid && isEmojiValid;

  const updateField = <Key extends keyof RecognitionValueFormState>(
    key: Key,
    value: RecognitionValueFormState[Key]
  ) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const toggleCard = (cardId: string) => {
    setForm((previous) => ({
      ...previous,
      cardIds: previous.cardIds.includes(cardId)
        ? previous.cardIds.filter((id) => id !== cardId)
        : [...previous.cardIds, cardId],
    }));
  };

  const uploadFile = async (file: File) => {
    if (!isAllowedCardImage(file)) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Unsupported file',
        description: 'Cards must be a PNG, JPG or JPEG image.',
      });
      return;
    }

    setIsUploadingCard(true);
    try {
      const newCardId = await onUploadCard(file);
      if (newCardId) {
        setForm((previous) => ({ ...previous, cardIds: [...previous.cardIds, newCardId] }));
      }
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not upload card',
        description: 'The image could not be uploaded. Please try again.',
      });
    } finally {
      setIsUploadingCard(false);
    }
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again still triggers a change event.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (file) {
      await uploadFile(file);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!isFormValid) {
      return;
    }

    await onSubmit({ ...form, name: form.name.trim(), description: form.description.trim() });
  };

  const heading = useMemo(() => (editingValue ? 'Edit Value' : 'Add Value'), [editingValue]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="recognition-value-modal">
        <ModalHeader>{heading}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDir="column" gap="16px">
            <FormControl isRequired isInvalid={submitAttempted && !isNameValid}>
              <FormLabel>Value name</FormLabel>
              <Input
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
                placeholder="Excellence"
                maxLength={100}
                data-testid="recognition-value-name-input"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description (optional)</FormLabel>
              <Textarea
                value={form.description}
                onChange={(event) => updateField('description', event.target.value)}
                placeholder="Describe what this value means..."
                maxLength={500}
                data-testid="recognition-value-description-input"
              />
            </FormControl>

            <EmojiPickerInput
              value={form.emoji}
              onChange={(emoji) => updateField('emoji', emoji)}
              isInvalid={submitAttempted && !isEmojiValid}
              errorMessage="Choose an emoji to represent this value."
            />

            <Flex align="center" justify="space-between">
              <Box>
                <Text textStyle="smallMedium">Allow GIFs</Text>
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Let senders attach a GIF when using this value.
                </Text>
              </Box>
              <Switch
                isChecked={form.allowGifs}
                onChange={(event) => updateField('allowGifs', event.target.checked)}
                aria-label="Allow GIFs"
                data-testid="recognition-value-allow-gifs-switch"
              />
            </Flex>

            {showEnabledToggle && (
              <Flex align="center" justify="space-between">
                <Box>
                  <Text textStyle="smallMedium">Enabled</Text>
                  <Text textStyle="extraSmallRegular" color="brand.darkGray">
                    Make this value available when sending recognition.
                  </Text>
                </Box>
                <Switch
                  isChecked={form.enabled}
                  onChange={(event) => updateField('enabled', event.target.checked)}
                  aria-label="Enabled"
                  data-testid="recognition-value-enabled-switch"
                />
              </Flex>
            )}

            <FormControl>
              <FormLabel>Cards</FormLabel>
              <Text textStyle="extraSmallRegular" color="brand.darkGray" mb="8px">
                Select the cards shown for this value, or upload a new one. Cards can be reused
                across values.
              </Text>
              <Grid templateColumns="repeat(4, 1fr)" gap="8px" data-testid="recognition-card-grid">
                {availableCards.map((card, index) => {
                  const isSelected = form.cardIds.includes(card.id);
                  return (
                    <Box
                      key={card.id}
                      as="button"
                      type="button"
                      position="relative"
                      borderWidth="3px"
                      borderRadius="8px"
                      borderColor={isSelected ? 'brand.purple' : 'transparent'}
                      boxShadow={
                        isSelected ? 'none' : 'inset 0 0 0 1px var(--chakra-colors-brand-lightGray)'
                      }
                      opacity={isSelected ? 1 : 0.55}
                      transition="opacity 0.15s ease, border-color 0.15s ease"
                      _hover={{ opacity: 1 }}
                      overflow="hidden"
                      onClick={() => toggleCard(card.id)}
                      aria-pressed={isSelected}
                      aria-label={`${isSelected ? 'Deselect' : 'Select'} card ${index + 1}`}
                      data-testid={`recognition-card-option-${card.id}`}
                    >
                      <Image src={card.contentUrl} alt="" h="64px" w="full" objectFit="cover" />
                      {isSelected && (
                        <Center
                          position="absolute"
                          top="4px"
                          right="4px"
                          boxSize="18px"
                          borderRadius="full"
                          bg="brand.purple"
                          color="white"
                          fontSize="11px"
                          fontWeight="bold"
                          lineHeight="1"
                        >
                          ✓
                        </Center>
                      )}
                    </Box>
                  );
                })}

                <Center
                  as="button"
                  type="button"
                  h="64px"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderRadius="8px"
                  borderColor={isDragging ? 'brand.purple' : 'brand.lightGray'}
                  bg={isDragging ? 'brand.lightPurple' : 'transparent'}
                  flexDir="column"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  aria-label="Upload a new card image"
                  data-testid="recognition-card-upload-tile"
                >
                  {isUploadingCard ? (
                    <Spinner size="sm" color="brand.purple" />
                  ) : (
                    <>
                      <Text fontSize="20px" lineHeight="1" color="brand.darkGray">
                        +
                      </Text>
                      <Text textStyle="extraSmallRegular" color="brand.darkGray">
                        Upload
                      </Text>
                    </>
                  )}
                </Center>
              </Grid>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                display="none"
                onChange={handleFileInputChange}
                data-testid="recognition-card-file-input"
              />
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr="12px"
            onClick={onClose}
            data-testid="recognition-value-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isSaving}
            isDisabled={!isFormValid}
            data-testid="recognition-value-save-button"
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RecognitionValueModal;
